import { CircleAlert } from 'lucide-react';
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../cx.js';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  /** Testo di aiuto sotto il campo, in ink-muted. */
  hint?: string;
  /** Messaggio di errore: bordo e testo in `error`, con icona. */
  error?: string;
  /** Elemento a destra dentro il campo (es. bottone mostra/nascondi). */
  trailing?: ReactNode;
  /** Testo sotto il campo allineato a destra (es. contatore). */
  meta?: ReactNode;
  mono?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, trailing, meta, mono, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-aiuto`;
  const errorId = `${inputId}-errore`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      <label htmlFor={inputId} className="t-label text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx(
            'w-full h-12 rounded-md bg-surface text-ink px-4 border',
            'placeholder:text-ink-muted',
            error ? 'border-error' : 'border-border-strong',
            trailing ? 'pr-14' : null,
            mono ? 't-code' : 't-body',
          )}
          {...rest}
        />
        {trailing && <div className="absolute inset-y-0 right-1 flex items-center">{trailing}</div>}
      </div>
      {(error || hint || meta) && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            {error && (
              <p id={errorId} role="alert" className="t-body-small text-error flex items-start gap-1">
                <CircleAlert aria-hidden size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </p>
            )}
            {hint && (
              <p id={hintId} className="t-body-small text-ink-muted">
                {hint}
              </p>
            )}
          </div>
          {meta && <div className="t-body-small text-ink-muted shrink-0">{meta}</div>}
        </div>
      )}
    </div>
  );
});
